import type { FC } from "react";
import { useEffect, useRef } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";

import { FetchGet } from "@/lib/fetch-helper";
import BlogPost, { type IBlogPost } from "@/src/blog/BlogPost";
import type { IBreadcrumbItem } from "@/src/common/Breadcrumb";
import type { IEntryPointComponent } from "@/src/common/PageTransition";
import { usePageContext } from "@/src/contexts/PageContext";
import { useQueryPageCustom } from "@/src/query-client-factory";
import { useQueryCustomNoSuspense } from "@/src/query-client-overrides";
import { browserStatics } from "@/src/shared/BrowserStatic";
import PageLayout from "@/src/shared/page-layout";
import MainLoading from "@Components/MainLoading";
import { urlFormat } from "@Components/Modules/Helpers";
import { BlogApi, BlogIndexMobile } from "@Routes/BlogLinks";
import { BlogPostMobile } from "@Routes/BlogLinks";
import * as stylex from "@stylexjs/stylex";

import { colors } from "../../styles/tokens.stylex";
import { type IArticle, PopularArticles } from "./PopularArticles";
import PostsList, { type IPostsList } from "./PostsList";

import * as cssPortal from "../shared/Portal.module.css";
import * as css from "./Blog.module.css";

export interface IBlog extends IEntryPointComponent {
    post: IBlogPost;
    postsList: IPostsList;
    popularArticles: IArticle[];
}

let initialRender = true;
const BlogPostPath = { ...BlogPostMobile, url: "/blog/%7Bslug%7D/%7Bid%7D" };

export const Blog: FC = () => {
    const { isMobileApp } = browserStatics;
    const { pathname } = useLocation();
    const [searchParams] = useSearchParams();
    const { p1, p2, p3 } = useParams();
    const page = Number(searchParams.get("page") || "1");

    const endpointRef = useRef(pathname);
    const { pageTitle, setPageContext, canonicalUrl } = usePageContext();

    const { data } = useQueryPageCustom<IBlog>({ ignoreQuery: true, endpoint: endpointRef.current });
    const props = data.component;

    const blogRoot = isMobileApp ? BlogIndexMobile : "/blog";

    useEffect(() => {
        initialRender = false;
        const animationFrame: PropertyIndexedKeyframes = {
            backgroundColor: ["#fff", "#f8f8f8"],
        } as PropertyIndexedKeyframes;

        const animationOptions: KeyframeAnimationOptions = {
            duration: 300,
            direction: "normal",
            fill: "forwards",
        };

        document.body.animate(animationFrame, animationOptions);

        return () => {
            document.body.animate(animationFrame, {
                ...animationOptions,
                direction: "reverse",
            });
        };
    }, []);

    const isBlogPost = !!(p1 && p2 && !p3);

    const { data: blogData, isLoading } = useQueryCustomNoSuspense({
        queryKey: ["blog", pathname, page, p1, p2, isBlogPost, p3],
        queryFn: () =>
            FetchGet<IPostsList | IBlogPost>(
                urlFormat(
                    BlogApi,
                    { action: isBlogPost ? "get" : "list", page, search: p1, id: isBlogPost ? p2 : p3 },
                    true,
                ),
            ),
        initialData: initialRender ? props.post || props.postsList : undefined,
    });

    useEffect(() => {
        if (!blogData) return;

        let cUrl = blogRoot;

        if (isBlogPost) {
            cUrl = urlFormat({ ...BlogPostMobile, url: "/blog/%7Bslug%7D/%7Bid%7D" }, { id: p2, slug: p1 });
        }

        if (pageTitle !== blogData.title || cUrl !== canonicalUrl) {
            setPageContext({
                ...data,
                pageTitle: blogData.title,
                canonicalUrl: canonicalUrl,
            });
        }
    }, [blogData, blogRoot, canonicalUrl, data, isBlogPost, p1, p2, pageTitle, setPageContext]);

    const routeContent = isLoading ? (
        <MainLoading {...stylex.props(styles.loading)} />
    ) : isBlogPost ? (
        <BlogPost {...(blogData as IBlogPost)} key={pathname} />
    ) : (
        <PostsList {...(blogData as IPostsList)} page={page} key={pathname} />
    );

    const children = (
        <div className={`${css.blogLayoutComponent} ${cssPortal.standardContainerV2}`}>
            <div className={`${css.blogContainer} ${cssPortal.setSectionColOne}`}>
                <h1 className={cssPortal.h3Font} dangerouslySetInnerHTML={{ __html: pageTitle }} />
                <hr />
                <div className={css.content}>{routeContent}</div>
                <div className={css.sidePanel}>
                    <PopularArticles
                        data={props.popularArticles}
                        urlTemplate={isMobileApp ? BlogPostMobile : BlogPostPath}
                    />
                </div>
            </div>
        </div>
    );

    if (isMobileApp) {
        return children;
    }

    const breadcrumbs: IBreadcrumbItem[] = isBlogPost
        ? [{ label: "Blog", path: "/blog" }, { label: pageTitle }]
        : [{ label: "Blog" }];

    return (
        <PageLayout
            breadcrumbs={breadcrumbs}
            layoutAdUnitPath="blog"
            slugTarget={"/blog"}
            adTagStyleX={styles.adBackground}
            isNewGrid
        >
            {children}
        </PageLayout>
    );
};

const styles = stylex.create({
    adBackground: {
        backgroundColor: colors.b_Emphasis,
    },
    loading: {
        paddingTop: "20vh",
    },
});
